import React, { useCallback, useState } from "react";
import { Button, Callout } from "@blueprintjs/core";
import { Col, Row } from "react-grid-system";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Field, useForm } from "react-final-form";
import axios from "axios";
import Fade from "../animate/Fade";
import { Error, required } from "./Validation";

const CreditCard = ({ price, packingCost, estimateId, id, handleSuccess }) => {
  const {
    getFieldState,
    getState,
    mutators: { setFieldTouched, setFieldValue },
  } = useForm();

  const fields = {
    cardHolder: getFieldState("cardHolder"),
    cardNumber: getFieldState("cardNumber"),
    cardExpiry: getFieldState("cardExpiry"),
    cardCcv: getFieldState("cardCcv"),
  };

  const { values } = getState();

  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("Secure funds");
  const [errorMessage, setErrorMessage] = useState("");
  const [errors, setErrors] = useState([]);
  const [fundsSecured, setFundsSecured] = useState(false);

  const handleSecureFunds = useCallback(async () => {
    setFieldTouched("cardHolder", true);
    setFieldTouched("cardNumber", true);
    setFieldTouched("cardExpiry", true);
    setFieldTouched("cardCcv", true);

    if (
      !fields.cardHolder.valid ||
      !fields.cardNumber.valid ||
      !fields.cardExpiry.valid ||
      !fields.cardCcv.valid
    ) {
      setFieldTouched("secured_funds", true);
      return;
    }

    const {
      cardNumber,
      add_packing,
      billing_firstname,
      billing_lastname,
      email,
      address_1,
      city,
      state,
      cardExpiry,
    } = values;

    setLoading(true);
    const amount = (add_packing ? price + packingCost : price).toFixed(2);
    const expirationDate = (cardExpiry || " ").split("-");
    const obj = {
      createTransactionRequest: {
        merchantAuthentication: {
          name: process.env.REACT_APP_AUTHORIZE_NET_LOGIN_ID,
          transactionKey: process.env.REACT_APP_AUTHORIZE_NET_TRANSACTION_KEY,
        },
        transactionRequest: {
          transactionType: "authOnlyTransaction",
          amount,
          payment: {
            creditCard: {
              cardNumber: cardNumber.replace(/\s/g, ""),
              expirationDate: `20${expirationDate[1] || "00"}-${
                expirationDate[0]
              }`,
              cardCode: "999",
            },
          },
          lineItems: {
            lineItem: {
              itemId: "1",
              name: "NVL - Book moving",
              description: `Pre approve funds for quote no. ${estimateId}`,
              quantity: "1",
              unitPrice: amount,
            },
          },
          // tax: {
          //   amount: "4.26",
          //   name: "level2 tax name",
          //   description: "level2 tax",
          // },
          customer: {
            id,
          },
          billTo: {
            firstName: billing_firstname,
            lastName: billing_lastname,
            address: address_1,
            city,
            state,
            // zip: "44628",
            country: "US",
          },
          shipTo: {
            firstName: billing_firstname,
            lastName: billing_lastname,
            address: address_1,
            city,
            state,
            country: "US",
          },
          // customerIP: "192.168.1.1",
          userFields: {
            userField: [
              {
                name: "MerchantDefinedFieldName1",
                value: "MerchantDefinedFieldValue1",
              },
              {
                name: "Email",
                value: email,
              },
            ],
          },
          authorizationIndicatorType: {
            authorizationIndicator: "pre",
          },
        },
      },
    };
    try {
      const res = await axios.post(process.env.REACT_APP_AUTHORIZE_NET, obj);
      if (
        res.status === 200 &&
        res.data?.messages?.message?.[0]?.code === "I00001"
      ) {
        setFieldValue("paymenttoken", res.data.transactionResponse.transId);
        setText("Funds secured");
        handleSuccess(true);
        setFundsSecured(true);
        setErrorMessage("");
      } else {
        handleSuccess(false);
        setErrorMessage(
          (res.data?.messages?.message || [])
            .map((message) => message.text)
            .join(", ")
        );
        setErrors(res.data.transactionResponse.errors || []);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [values, fields]);

  return (
    <Row>
      <Field
        name="paymenttoken"
        id="paymenttoken"
        type="hidden"
        component="input"
      />
      <Col sm={12} className="form-fields__block">
        <label htmlFor="cardHolder">
          Cardholder Name
          <Field
            id="cardHolder"
            name="cardHolder"
            component="input"
            type="text"
            disabled={fundsSecured}
            className="input"
            validate={required()}
          />
        </label>
        <Error name="cardHolder" />
      </Col>
      <Col sm={12} md={7} className="form-fields__block">
        <label htmlFor="cardNumber">
          Card Number
          <Field name="cardNumber" validate={required()}>
            {({ input }) => {
              return (
                <input
                  disabled={fundsSecured}
                  className="input"
                  onChange={input.onChange}
                  value={(
                    input.value.replace(/\s/g, "").match(/.{1,4}/g) || []
                  ).join(" ")}
                />
              );
            }}
          </Field>
        </label>
        <Error name="cardNumber" />
      </Col>
      <Col sm={6} md={3} className="form-fields__block">
        <label htmlFor="cardExpiry">
          Expiry
          <Field name="cardExpiry" validate={required()}>
            {({ input }) => (
              <input
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...input}
                className="input"
                onChange={({ target: { value } }) => {
                  let newValue = value.toString();
                  newValue =
                    newValue.length === 3 && newValue[2] === "-"
                      ? newValue
                      : (
                          newValue.replace(/\D/g, "").match(/.{1,2}/g) || []
                        ).join("-");
                  input.onChange(newValue.substring(0, 5));
                }}
                placeholder="MM-YY"
                disabled={fundsSecured}
              />
            )}
          </Field>
        </label>
        <Error name="cardExpiry" />
      </Col>
      <Col sm={6} md={2} className="form-fields__block">
        <label htmlFor="cardCcv">
          CCV
          <Field name="cardCcv" type="text" validate={required()}>
            {({ input }) => (
              <input
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...input}
                className="input"
                disabled={fundsSecured}
                value={input.value.replace(/\D/g, "").substring(0, 4)}
              />
            )}
          </Field>
        </label>
        <Error name="cardCcv" />
      </Col>
      <Col sm={12} className="form-fields__block">
        <Fade show={!!errorMessage}>
          <Callout intent="danger" title={errorMessage}>
            {errors && (
              <ul>
                {errors.map((e) => (
                  <li key={e.errorText}>{e.errorText}</li>
                ))}
              </ul>
            )}
          </Callout>
        </Fade>
        {!errorMessage && <Error name="secured_funds" />}
      </Col>
      <Col sm={12} className="form-fields__block payment">
        <Button
          className="button"
          onClick={handleSecureFunds}
          loading={loading}
          disabled={fundsSecured}
        >
          {text}
        </Button>
      </Col>
    </Row>
  );
};

CreditCard.propTypes = {
  packingCost: PropTypes.number,
  price: PropTypes.number,
  estimateId: PropTypes.number,
  id: PropTypes.number,
  handleSuccess: PropTypes.func.isRequired,
};

CreditCard.defaultProps = {
  packingCost: 0,
  price: 0,
  estimateId: 0,
  id: 0,
};

const mapDispatchToProps = () => ({});

const mapStateToProps = (state) => {
  return {
    price: state.estimate.estimate,
    packingCost: state.estimate.packing_cost,
    estimateId: state.estimate.estimate,
    id: state.estimate.id,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditCard);
